import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertCircle } from 'lucide-react'
import { getDraftFile, putDraftFile } from '@/api/draftFiles'
import { Banner } from '@/components/Banner'
import { STALE_DRAFT_SAVE_REASON, StaleDraftBanner } from '@/components/StaleDraftBanner'
import { Tooltip } from '@/components/Tooltip'
import { useServerSeed } from '@/hooks/useServerSeed'
import { errorMessage, formatSaveError } from '@/lib/apiErrors'
import { refreshAfterWrite } from '@/lib/refreshAfterWrite'
import { hasRangeErrors } from '@/lib/settingRanges'
import type { GlobalDefaultsFile } from '@/types/bundle'
import { AcceptanceTab } from './components/AcceptanceTab'
import { NormalisationTab } from './components/NormalisationTab'
import { LimitsOutputTab } from './components/LimitsOutputTab'
import { ChunkingTab } from './components/ChunkingTab'
import { PageHintRetryTab } from './components/PageHintRetryTab'
import { GovernanceTab } from './components/GovernanceTab'

function SkeletonBlock() {
  return <div className="h-96 animate-pulse rounded-control bg-surface-raised" />
}

type Tab = 'acceptance' | 'normalisation' | 'limits' | 'page_hints' | 'chunking' | 'governance'

const TABS: { key: Tab; label: string }[] = [
  { key: 'acceptance', label: 'Acceptance' },
  { key: 'normalisation', label: 'Normalisation' },
  { key: 'limits', label: 'Limits & Output' },
  { key: 'page_hints', label: 'Page Hints' },
  { key: 'chunking', label: 'Chunking' },
  { key: 'governance', label: 'Override Governance' },
]

const QUERY_KEY = ['draft-file', 'global_defaults']

// Single-file settings screen over global_defaults.json - one whole-file
// draft/save, same PUT /gui/v1/drafts/current/files/{file_key} route every
// other screen already uses (drafts.py has no per-file allowlist), split
// into tabs by section rather than Document Types' list+detail shape, since
// this file has exactly one instance, not many keyed entries.
export function GlobalConfigsScreen() {
  const queryClient = useQueryClient()
  const globalDefaultsQuery = useQuery({
    queryKey: QUERY_KEY,
    queryFn: () => getDraftFile<GlobalDefaultsFile>('global_defaults'),
  })

  const [draft, setDraft] = useState<GlobalDefaultsFile | null>(null)
  const [savedSnapshot, setSavedSnapshot] = useState<GlobalDefaultsFile | null>(null)
  const [tab, setTab] = useState<Tab>('acceptance')

  // Seeds the editable draft, and re-seeds it whenever the server copy
  // changes (a Diff & Validation discard, publish, another tab) unless there
  // are unsaved edits.
  const serverSeed = useServerSeed(QUERY_KEY, globalDefaultsQuery, {
    seed: (data) => {
      setDraft(data)
      setSavedSnapshot(data)
    },
    isDirty: () => JSON.stringify(draft) !== JSON.stringify(savedSnapshot),
  })

  const saveMutation = useMutation({
    mutationFn: (payload: GlobalDefaultsFile) => putDraftFile<GlobalDefaultsFile>('global_defaults', payload),
    onSuccess: (_result, payload) => {
      setDraft(payload)
      setSavedSnapshot(payload)
      serverSeed.noteSaved(payload)
      refreshAfterWrite(queryClient, { kind: 'draft-file', fileKey: 'global_defaults' })
    },
  })

  if (globalDefaultsQuery.isPending) {
    return <SkeletonBlock />
  }

  if (globalDefaultsQuery.isError) {
    return (
      <Banner variant="danger" icon={AlertCircle} title="Failed to load platform defaults">
        {errorMessage(globalDefaultsQuery.error)}
      </Banner>
    )
  }

  if (!draft || !savedSnapshot) {
    return <SkeletonBlock />
  }

  const isDirty = JSON.stringify(draft) !== JSON.stringify(savedSnapshot)

  function updateDraft(patch: Partial<GlobalDefaultsFile>) {
    setDraft((current) => (current ? { ...current, ...patch } : current))
  }

  async function handleSave() {
    if (!draft || !(await serverSeed.ensureCurrent())) return
    saveMutation.mutate(draft)
  }

  return (
    <div className="flex flex-col gap-6">
      <h1 className="text-title font-semibold text-text-primary">Global Configs</h1>

      {serverSeed.baseChanged && <StaleDraftBanner onReload={serverSeed.reload} />}

      {saveMutation.isError && (
        <Banner variant="danger" icon={AlertCircle} title="Save failed">
          {formatSaveError(saveMutation.error)}
        </Banner>
      )}

      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((option) => (
          <button
            key={option.key}
            type="button"
            onClick={() => setTab(option.key)}
            className={
              tab === option.key
                ? 'border-b-2 border-accent px-3 py-2 text-body font-medium text-text-primary'
                : 'border-b-2 border-transparent px-3 py-2 text-body font-medium text-text-secondary hover:text-text-primary'
            }
          >
            {option.label}
          </button>
        ))}
      </div>

      {tab === 'acceptance' && (
        <AcceptanceTab acceptance={draft.acceptance} onChange={(acceptance) => updateDraft({ acceptance })} />
      )}

      {tab === 'normalisation' && (
        <NormalisationTab
          normalisation={draft.normalisation ?? {}}
          onChange={(normalisation) => updateDraft({ normalisation })}
        />
      )}

      {tab === 'limits' && <LimitsOutputTab file={draft} onChange={updateDraft} />}

      {tab === 'page_hints' && (
        <PageHintRetryTab
          retry={draft.page_hint_retry ?? {}}
          onChange={(page_hint_retry) => updateDraft({ page_hint_retry })}
        />
      )}

      {tab === 'chunking' && (
        <ChunkingTab chunking={draft.chunking ?? {}} onChange={(chunking) => updateDraft({ chunking })} />
      )}

      {tab === 'governance' && (
        <GovernanceTab
          governance={draft.override_governance}
          onChange={(override_governance) => updateDraft({ override_governance })}
        />
      )}

      <div className="flex justify-end">
        {(() => {
          const disabledReason = !isDirty
            ? 'No changes to save'
            : serverSeed.baseChanged
              ? STALE_DRAFT_SAVE_REASON
              : hasRangeErrors(draft.chunking) || hasRangeErrors(draft.page_hint_retry)
              ? 'Fix the highlighted values before saving'
              : null

          if (disabledReason && !saveMutation.isPending) {
            return (
              <Tooltip text={disabledReason}>
                <button
                  type="button"
                  aria-disabled="true"
                  className="cursor-not-allowed rounded-control bg-accent/50 px-4 py-2 text-body font-medium text-white/70"
                >
                  Save
                </button>
              </Tooltip>
            )
          }

          return (
            <button
              type="button"
              disabled={saveMutation.isPending}
              onClick={handleSave}
              className="rounded-control bg-accent px-4 py-2 text-body font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {saveMutation.isPending ? 'Saving…' : 'Save'}
            </button>
          )
        })()}
      </div>
    </div>
  )
}
